import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import {
  myCompanion, generateCompanionCode, joinCompanion, leaveCompanion,
  myCircles, createCircle, updateCircle, deleteCircle, joinCircleByCode,
  addCircleMember, removeCircleMember,
  listCommunityFeedback, submitCommunityFeedback,
  myCompanionRequests, acceptCompanionRequest, rejectCompanionRequest,
  circleJoinRequests, acceptCircleJoinRequest, rejectCircleJoinRequest,
  myCircleInvites, acceptCircleInvite, rejectCircleInvite,
  myOrganizations, joinOrganization, orgJoinRequests, acceptOrgJoinRequest, rejectOrgJoinRequest,
  orgMembers, removeOrgMember, setOrgMemberRole,
} from "@/lib/apiClient";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { checkAchievements } from "@/lib/achievements";
import { PeerReviewPanel } from "@/pages/PeerReview";
import {
  Users, UserPlus, Copy, LogOut, Plus, Trash2, Pencil, X, Check,
  Star, Clock, MessageSquare, Inbox, Building2,
} from "lucide-react";

const CIRCLE_TYPES = [
  { value: "family", labelKey: "community_page.circle_type_family" },
  { value: "friends", labelKey: "community_page.circle_type_friends" },
  { value: "masjid", labelKey: "community_page.circle_type_masjid" },
  { value: "school", labelKey: "community_page.circle_type_school" },
  { value: "other", labelKey: "community_page.circle_type_other" },
];

const TABS = ["companion", "circles", "organization", "requests", "peer", "feedback"];

export default function Community() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const tab = TABS.includes(params.get("tab")) ? params.get("tab") : "companion";
  const setTab = (next) => setParams((p) => { const np = new URLSearchParams(p); np.set("tab", next); return np; }, { replace: true });
  const tabLabels = {
    companion: t("community_page.tab_companion"),
    circles: t("community_page.tab_circles"),
    organization: t("community_page.tab_organization"),
    requests: t("community_page.tab_requests"),
    peer: t("community_page.tab_peer"),
    feedback: t("community_page.tab_feedback"),
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="community-page">
      <Header />
      <main className="flex-1 max-w-[896px] mx-auto w-full px-5 sm:px-8 py-12">
        <div className="text-xs uppercase tracking-[0.22em] text-[#D97706] font-semibold">{t("community_page.eyebrow")}</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-2 text-[#1F2937]">{t("community_page.title")}</h1>
        <p className="text-[#4B5563] mt-3 max-w-xl">{t("community_page.subtitle")}</p>

        <div className="mt-6 inline-flex flex-wrap items-center rounded-full border border-[#E7E0D2] bg-white overflow-hidden" data-testid="community-tabs">
          {TABS.map((tb) => (
            <button key={tb} data-testid={`community-tab-${tb}`} onClick={() => setTab(tb)}
              className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium capitalize transition ${tab === tb ? "bg-[#064E3B] text-white" : "text-[#4B5563] hover:text-[#1F2937]"}`}>
              {tabLabels[tb] || tb}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "companion" && <CompanionPanel />}
          {tab === "circles" && <CirclesPanel />}
          {tab === "organization" && <OrganizationPanel />}
          {tab === "requests" && <RequestsPanel />}
          {tab === "peer" && <PeerReviewPanel />}
          {tab === "feedback" && <FeedbackPanel />}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function CompanionPanel() {
  const { t } = useI18n();
  const [companion, setCompanion] = useState(undefined); // undefined = loading
  const [code, setCode] = useState(null);
  const [joinCode, setJoinCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => myCompanion().then((r) => setCompanion(r.companion)).catch(() => setCompanion(null));
  useEffect(() => { load(); }, []);

  const getCode = async () => {
    setBusy(true);
    try {
      const r = await generateCompanionCode();
      setCode(r.code);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate a code");
    } finally { setBusy(false); }
  };

  const join = async () => {
    if (!joinCode.trim()) return;
    setBusy(true);
    try {
      await joinCompanion(joinCode.trim().toUpperCase());
      toast.success("Request sent — waiting for them to accept.");
      setJoinCode("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not join with that code");
    } finally { setBusy(false); }
  };

  const leave = async () => {
    setBusy(true);
    try {
      await leaveCompanion();
      setCompanion(null);
      toast.success("Companion removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove companion");
    } finally { setBusy(false); }
  };

  if (companion === undefined) return <div className="h-32 bg-[#E7E0D2] rounded-2xl animate-pulse" />;

  if (companion) {
    return (
      <div className="rounded-2xl bg-white border border-[#E7E0D2] p-6" data-testid="companion-active">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-[#D97706] text-white grid place-items-center"><Users className="w-6 h-6" /></div>
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-[#4B5563]">Your Quran Companion</div>
            <div className="font-heading text-lg text-[#1F2937]">{companion.other_user?.name || companion.other_user?.email || "Companion"}</div>
          </div>
        </div>
        <button data-testid="companion-leave" onClick={leave} disabled={busy}
          className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#E7E0D2] hover:border-[#EF4444] text-[#EF4444] text-sm font-medium transition disabled:opacity-50">
          <LogOut className="w-4 h-4" /> Remove companion
        </button>
      </div>
    );
  }

  return (
    <div className="grid sm:grid-cols-2 gap-4" data-testid="companion-setup">
      <div className="rounded-2xl bg-white border border-[#E7E0D2] p-6">
        <div className="text-sm font-semibold text-[#1F2937]">{t("community_page.invite_title")}</div>
        <p className="text-xs text-[#4B5563] mt-1">{t("community_page.invite_body")}</p>
        {code ? (
          <div className="mt-4 flex items-center gap-2">
            <div className="font-heading text-2xl tracking-[0.2em] text-[#064E3B]" data-testid="companion-code">{code}</div>
            <button onClick={() => { navigator.clipboard?.writeText(code); toast.success("Copied"); }}
              className="p-2 rounded-full hover:bg-[#F9F5EE] text-[#4B5563]" title="Copy code"><Copy className="w-4 h-4" /></button>
          </div>
        ) : (
          <button data-testid="companion-generate" onClick={getCode} disabled={busy}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white text-sm font-medium transition disabled:opacity-50">
            <UserPlus className="w-4 h-4" /> {t("community_page.generate_code")}
          </button>
        )}
      </div>
      <div className="rounded-2xl bg-white border border-[#E7E0D2] p-6">
        <div className="text-sm font-semibold text-[#1F2937]">{t("community_page.have_code_title")}</div>
        <p className="text-xs text-[#4B5563] mt-1">{t("community_page.have_code_body")}</p>
        <div className="mt-4 flex items-center gap-2">
          <input data-testid="companion-join-input" value={joinCode} onChange={(e) => setJoinCode(e.target.value)}
            placeholder={t("community_page.code_placeholder")} className="flex-1 px-3 py-2 rounded-lg border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm uppercase" />
          <button data-testid="companion-join-btn" onClick={join} disabled={busy || !joinCode.trim()}
            className="px-4 py-2 rounded-full bg-[#D97706] hover:bg-[#B45309] disabled:opacity-40 text-white text-sm font-medium transition">{t("community_page.join")}</button>
        </div>
      </div>
    </div>
  );
}

function CirclesPanel() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [circles, setCircles] = useState(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState("family");
  const [customLabel, setCustomLabel] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => myCircles().then((r) => setCircles(r.circles || [])).catch(() => setCircles([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await createCircle({ name: name.trim(), type, custom_label: type === "other" ? customLabel.trim() : undefined });
      toast.success(t("community_page.toast_circle_created"));
      setName(""); setCustomLabel(""); setCreating(false);
      checkAchievements();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("community_page.toast_circle_create_failed"));
    } finally { setBusy(false); }
  };

  const join = async () => {
    if (!joinCode.trim()) return;
    setBusy(true);
    try {
      const r = await joinCircleByCode(joinCode.trim().toUpperCase());
      toast.success(r.already_requested ? t("community_page.toast_request_already_sent_owner") : t("community_page.toast_request_sent_circle_owner"));
      setJoinCode("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("community_page.toast_join_code_failed"));
    } finally { setBusy(false); }
  };

  if (!circles) return <div className="h-32 bg-[#E7E0D2] rounded-2xl animate-pulse" />;

  return (
    <div data-testid="circles-panel">
      <div className="rounded-2xl bg-white border border-[#E7E0D2] p-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="text-sm font-semibold text-[#1F2937]">{t("community_page.join_circle_with_code")}</div>
          <div className="flex items-center gap-2">
            <input data-testid="circle-join-input" value={joinCode} onChange={(e) => setJoinCode(e.target.value)}
              placeholder={t("community_page.invite_code_placeholder")} className="px-3 py-2 rounded-lg border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm uppercase w-36" />
            <button data-testid="circle-join-btn" onClick={join} disabled={busy || !joinCode.trim()}
              className="px-4 py-2 rounded-full bg-[#D97706] hover:bg-[#B45309] disabled:opacity-40 text-white text-sm font-medium transition">{t("community_page.join")}</button>
          </div>
        </div>
      </div>

      <div className="mt-4">
        {!creating ? (
          <button data-testid="circle-create-open" onClick={() => setCreating(true)}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white text-sm font-medium transition">
            <Plus className="w-4 h-4" /> {t("community_page.create_a_circle")}
          </button>
        ) : (
          <div className="rounded-2xl bg-white border border-[#E7E0D2] p-6" data-testid="circle-create-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-[0.16em] text-[#4B5563]">{t("community_page.name")}</label>
                <input data-testid="circle-name-input" value={name} onChange={(e) => setName(e.target.value)}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.16em] text-[#4B5563]">{t("community_page.type")}</label>
                <select data-testid="circle-type-select" value={type} onChange={(e) => setType(e.target.value)}
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm">
                  {CIRCLE_TYPES.map((o) => <option key={o.value} value={o.value}>{t(o.labelKey)}</option>)}
                </select>
              </div>
              {type === "other" && (
                <div className="sm:col-span-2">
                  <label className="text-xs uppercase tracking-[0.16em] text-[#4B5563]">{t("community_page.describe_the_group")}</label>
                  <input data-testid="circle-custom-label-input" value={customLabel} onChange={(e) => setCustomLabel(e.target.value)}
                    className="mt-1 w-full px-3 py-2 rounded-lg border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm" />
                </div>
              )}
            </div>
            <div className="mt-4 flex items-center gap-2">
              <button data-testid="circle-create-submit" onClick={create} disabled={busy || !name.trim()}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#064E3B] hover:bg-[#047857] disabled:opacity-40 text-white text-sm font-medium transition">
                <Check className="w-4 h-4" /> {t("community_page.create")}
              </button>
              <button onClick={() => setCreating(false)} className="px-4 py-2 rounded-full border border-[#E7E0D2] text-[#4B5563] text-sm hover:border-[#064E3B] transition">{t("community_page.cancel")}</button>
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-col gap-4">
        {circles.map((c) => <CircleCard key={c.circle_id} circle={c} currentUserId={user?.user_id} onChanged={load} />)}
      </div>
    </div>
  );
}

function CircleCard({ circle, currentUserId, onChanged }) {
  const { t } = useI18n();
  const isOwner = circle.owner_id === currentUserId;
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(circle.name);
  const [addEmail, setAddEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const typeLabel = circle.type === "other" ? (circle.custom_label || t("community_page.circle_type_other")) : t(CIRCLE_TYPES.find((ct) => ct.value === circle.type)?.labelKey || "community_page.circle_type_other");

  const saveName = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await updateCircle(circle.circle_id, { name: name.trim() });
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("community_page.toast_circle_update_failed"));
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await deleteCircle(circle.circle_id);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("community_page.toast_circle_delete_failed"));
    } finally { setBusy(false); }
  };

  const addMember = async () => {
    if (!addEmail.trim()) return;
    setBusy(true);
    try {
      const r = await addCircleMember(circle.circle_id, addEmail.trim().toLowerCase());
      setAddEmail("");
      toast.success(r.already_invited ? t("community_page.toast_invite_already_sent") : t("community_page.toast_invite_sent"));
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("community_page.toast_add_member_failed"));
    } finally { setBusy(false); }
  };

  const removeMember = async (userId) => {
    setBusy(true);
    try {
      await removeCircleMember(circle.circle_id, userId);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("community_page.toast_remove_member_failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl bg-white border border-[#E7E0D2] p-6" data-testid={`circle-${circle.circle_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="flex items-center gap-2">
              <input value={name} onChange={(e) => setName(e.target.value)} className="px-2 py-1 rounded-lg border border-[#E7E0D2] text-sm font-heading" />
              <button onClick={saveName} disabled={busy} className="p-1.5 rounded-full hover:bg-[#F9F5EE] text-[#064E3B]"><Check className="w-4 h-4" /></button>
              <button onClick={() => { setEditing(false); setName(circle.name); }} className="p-1.5 rounded-full hover:bg-[#F9F5EE] text-[#4B5563]"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="font-heading text-lg text-[#1F2937]">{circle.name}</span>
              {isOwner && <button onClick={() => setEditing(true)} className="p-1 rounded-full hover:bg-[#F9F5EE] text-[#4B5563]"><Pencil className="w-3.5 h-3.5" /></button>}
            </div>
          )}
          <div className="mt-1 flex items-center gap-2 text-xs text-[#4B5563]">
            <span className="px-2 py-0.5 rounded-full bg-[#F5EFE3] capitalize">{typeLabel}</span>
            <span>{t("community_page.members_count", { count: circle.member_ids?.length || 0 })}</span>
            {isOwner && <span className="font-mono">{t("community_page.code_label")} {circle.invite_code}</span>}
          </div>
        </div>
        {isOwner && (
          <button data-testid={`circle-delete-${circle.circle_id}`} onClick={remove} disabled={busy}
            className="p-2 rounded-full hover:bg-red-50 text-[#EF4444]" title={t("community_page.delete_circle")}><Trash2 className="w-4 h-4" /></button>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {(circle.members || []).map((m) => (
          <span key={m.user_id} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#F9F5EE] text-xs text-[#1F2937]">
            {m.name || m.email || m.user_id}
            {isOwner && m.user_id !== circle.owner_id && (
              <button onClick={() => removeMember(m.user_id)} className="text-[#9CA3AF] hover:text-[#EF4444]"><X className="w-3 h-3" /></button>
            )}
          </span>
        ))}
      </div>

      {isOwner && (circle.member_ids?.length || 0) < 8 && (
        <div className="mt-4 flex items-center gap-2">
          <input data-testid={`circle-add-email-${circle.circle_id}`} value={addEmail} onChange={(e) => setAddEmail(e.target.value)}
            placeholder={t("community_page.invite_member_by_email")} className="flex-1 px-3 py-2 rounded-lg border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm" />
          <button onClick={addMember} disabled={busy || !addEmail.trim()}
            className="px-3 py-2 rounded-full bg-white border border-[#E7E0D2] hover:border-[#064E3B] text-sm text-[#1F2937] transition disabled:opacity-40">{t("community_page.invite")}</button>
        </div>
      )}
    </div>
  );
}

/**
 * Institutions (schools/masajids/madrasas/universities). Organizations are
 * admin-provisioned — a user can't create one here, only join with a code
 * their organization's owner shared. Owners/teachers see a roster + progress
 * dashboard (`OrgDashboard`); students see a simple read-only membership
 * card (`OrgMemberCard`). Org "teacher" is scoped to this org only — it
 * carries no platform-wide grading power.
 */
function OrganizationPanel() {
  const { t } = useI18n();
  const [orgs, setOrgs] = useState(null);
  const [joinCode, setJoinCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => myOrganizations().then((r) => setOrgs(r.organizations || [])).catch(() => setOrgs([]));
  useEffect(() => { load(); }, []);

  const join = async () => {
    if (!joinCode.trim()) return;
    setBusy(true);
    try {
      const r = await joinOrganization(joinCode.trim().toUpperCase());
      toast.success(r.already_requested ? t("community_page.toast_request_already_sent_owner") : t("community_page.toast_request_sent_org_owner"));
      setJoinCode("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("community_page.toast_join_code_failed"));
    } finally { setBusy(false); }
  };

  if (!orgs) return <div className="h-32 bg-[#E7E0D2] rounded-2xl animate-pulse" />;

  return (
    <div data-testid="organization-panel" className="flex flex-col gap-4">
      {orgs.length === 0 && (
        <div className="rounded-2xl bg-white border border-[#E7E0D2] p-6" data-testid="organization-join-form">
          <div className="text-sm font-semibold text-[#1F2937]">{t("community_page.join_your_organization")}</div>
          <p className="text-xs text-[#4B5563] mt-1">{t("community_page.join_org_hint")}</p>
          <div className="mt-4 flex items-center gap-2">
            <input data-testid="organization-join-input" value={joinCode} onChange={(e) => setJoinCode(e.target.value)}
              placeholder={t("community_page.invite_code_placeholder")} className="flex-1 px-3 py-2 rounded-lg border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm uppercase" />
            <button data-testid="organization-join-btn" onClick={join} disabled={busy || !joinCode.trim()}
              className="px-4 py-2 rounded-full bg-[#D97706] hover:bg-[#B45309] disabled:opacity-40 text-white text-sm font-medium transition">{t("community_page.join")}</button>
          </div>
        </div>
      )}

      {orgs.map((o) => (
        o.my_role === "student" ? <OrgMemberCard key={o.org_id} org={o} /> : <OrgDashboard key={o.org_id} org={o} />
      ))}
    </div>
  );
}

function OrgMemberCard({ org }) {
  const { t } = useI18n();
  return (
    <div className="rounded-2xl bg-white border border-[#E7E0D2] p-6" data-testid={`org-member-card-${org.org_id}`}>
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-[#064E3B] text-white grid place-items-center"><Building2 className="w-6 h-6" /></div>
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#4B5563]">{t("community_page.youre_part_of")}</div>
          <div className="font-heading text-lg text-[#1F2937]">{org.name}</div>
        </div>
      </div>
    </div>
  );
}

function OrgDashboard({ org }) {
  const { t } = useI18n();
  const [members, setMembers] = useState(null);
  const isOwner = org.my_role === "owner";

  const load = () => orgMembers(org.org_id).then((r) => setMembers(r.members || [])).catch(() => setMembers([]));
  useEffect(() => { load(); }, [org.org_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (userId) => {
    if (!window.confirm(t("community_page.confirm_remove_org_member"))) return;
    try { await removeOrgMember(org.org_id, userId); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("community_page.toast_remove_member_failed")); }
  };

  const changeRole = async (userId, newRole) => {
    try { await setOrgMemberRole(org.org_id, userId, newRole); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("community_page.toast_update_role_failed")); }
  };

  const avgCompletion = members && members.length > 0
    ? Math.round(100 * members.reduce((s, m) => s + m.modules_passed, 0) / (members.length * 9))
    : 0;

  return (
    <div className="rounded-2xl bg-white border border-[#E7E0D2] p-6" data-testid={`org-dashboard-${org.org_id}`}>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-[#4B5563]">{t("community_page.your_organization")} · {org.my_role}</div>
          <div className="font-heading text-xl text-[#1F2937]">{org.name}</div>
        </div>
        <div className="flex gap-5 text-xs">
          <div className="text-center">
            <div className="font-heading text-2xl text-[#064E3B] tabular-nums" data-testid={`org-member-count-${org.org_id}`}>{members?.length ?? "…"}</div>
            <div className="text-[#4B5563]">{t("community_page.members_label")}</div>
          </div>
          <div className="text-center">
            <div className="font-heading text-2xl text-[#064E3B] tabular-nums">{avgCompletion}%</div>
            <div className="text-[#4B5563]">{t("community_page.avg_completion")}</div>
          </div>
        </div>
      </div>

      {isOwner && (
        <div className="mt-4 flex flex-wrap gap-2 text-xs">
          <span className="px-2.5 py-1 rounded-full bg-[#FDFBF7] border border-[#E7E0D2] text-[#4B5563]">{t("community_page.teacher_invite_code")} <b className="text-[#1F2937]">{org.invite_code_teacher}</b></span>
          <span className="px-2.5 py-1 rounded-full bg-[#FDFBF7] border border-[#E7E0D2] text-[#4B5563]">{t("community_page.student_invite_code")} <b className="text-[#1F2937]">{org.invite_code_student}</b></span>
        </div>
      )}

      <div className="mt-5 flex flex-col gap-2">
        {members === null ? (
          <div className="h-16 bg-[#E7E0D2] rounded-2xl animate-pulse" />
        ) : members.length === 0 ? (
          <div className="text-center text-sm text-[#4B5563] py-6">{t("community_page.no_members_yet")}</div>
        ) : (
          members.map((m) => (
            <div key={m.user_id} className="flex items-center gap-3 rounded-xl bg-[#FDFBF7] border border-[#E7E0D2] px-4 py-3" data-testid={`org-member-${m.user_id}`}>
              <span className="flex-1 text-sm text-[#1F2937] truncate">{m.name || m.email || m.user_id}</span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#ECFDF5] text-[#064E3B] font-medium capitalize shrink-0">{m.org_role}</span>
              {m.org_role !== "owner" && (
                <span className="text-xs text-[#4B5563] shrink-0">{t("community_page.n_of_9_modules", { n: m.modules_passed })}</span>
              )}
              {isOwner && m.org_role !== "owner" && (
                <div className="flex items-center gap-1 shrink-0">
                  <select data-testid={`org-member-role-${m.user_id}`} value={m.org_role} onChange={(e) => changeRole(m.user_id, e.target.value)}
                    className="text-xs px-2 py-1 rounded-full border border-[#E7E0D2] bg-white">
                    <option value="student">{t("community_page.role_student")}</option>
                    <option value="teacher">{t("community_page.role_teacher")}</option>
                  </select>
                  <button data-testid={`org-member-remove-${m.user_id}`} onClick={() => remove(m.user_id)} className="p-1.5 rounded-full hover:bg-red-50 text-[#EF4444]" title={t("community_page.remove")}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

/**
 * All incoming, not-yet-decided items across both consent points:
 * companion pairing requests, circle join requests (for circles this user
 * owns), and circle invites (direct-adds this user needs to approve).
 */
function RequestsPanel() {
  const { t } = useI18n();
  const [companionReqs, setCompanionReqs] = useState(null);
  const [circleReqs, setCircleReqs] = useState(null); // [{circle, requests: [...]}]
  const [invites, setInvites] = useState(null);
  const [orgReqs, setOrgReqs] = useState(null); // [{org, requests: [...]}]
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    const [cr, myCircs, inv, myOrgs] = await Promise.all([
      myCompanionRequests().then((r) => r.items || []).catch(() => []),
      myCircles().then((r) => r.circles || []).catch(() => []),
      myCircleInvites().then((r) => r.items || []).catch(() => []),
      myOrganizations().then((r) => r.organizations || []).catch(() => []),
    ]);
    setCompanionReqs(cr);
    setInvites(inv);
    // circleJoinRequests() 403s for circles this user doesn't own — the
    // .catch(() => null) silently skips those rather than erroring.
    const results = await Promise.all(
      myCircs.map((c) => circleJoinRequests(c.circle_id).then((r) => ({ circle: c, requests: r.items || [] })).catch(() => null))
    );
    setCircleReqs(results.filter((r) => r && r.requests.length > 0));
    // Only organization owners can list join requests — same 403-skip pattern.
    const ownedOrgs = myOrgs.filter((o) => o.my_role === "owner");
    const orgResults = await Promise.all(
      ownedOrgs.map((o) => orgJoinRequests(o.org_id).then((r) => ({ org: o, requests: r.items || [] })).catch(() => null))
    );
    setOrgReqs(orgResults.filter((r) => r && r.requests.length > 0));
  };
  useEffect(() => { load(); }, []);

  const decide = async (fn, id, extra) => {
    setBusyId(id);
    try {
      await fn(...(extra || []), id);
      checkAchievements();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("community_page.toast_process_request_failed"));
    } finally { setBusyId(null); }
  };

  if (companionReqs === null || circleReqs === null || invites === null || orgReqs === null) {
    return <div className="h-32 bg-[#E7E0D2] rounded-2xl animate-pulse" />;
  }

  const nothing = companionReqs.length === 0 && circleReqs.length === 0 && invites.length === 0 && orgReqs.length === 0;

  return (
    <div data-testid="requests-panel" className="flex flex-col gap-6">
      {nothing && (
        <div className="rounded-2xl bg-white border border-[#E7E0D2] p-8 text-center text-sm text-[#4B5563]">
          <Inbox className="w-8 h-8 text-[#E7E0D2] mx-auto mb-3" />
          {t("community_page.nothing_waiting")}
        </div>
      )}

      {companionReqs.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-[0.16em] text-[#4B5563] mb-2">{t("community_page.companion_requests")}</div>
          <div className="flex flex-col gap-2">
            {companionReqs.map((r) => (
              <div key={r.request_id} className="rounded-2xl bg-white border border-[#E7E0D2] p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`companion-request-${r.request_id}`}>
                <span className="text-sm text-[#1F2937]">{t("community_page.wants_to_be_companion", { name: r.from_user?.name || r.from_user?.email || t("community_page.someone") })}</span>
                <div className="flex items-center gap-2">
                  <button data-testid={`companion-request-accept-${r.request_id}`} disabled={busyId === r.request_id}
                    onClick={() => decide(acceptCompanionRequest, r.request_id)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white text-xs font-medium transition disabled:opacity-50">
                    <Check className="w-3.5 h-3.5" /> {t("community_page.accept")}
                  </button>
                  <button data-testid={`companion-request-reject-${r.request_id}`} disabled={busyId === r.request_id}
                    onClick={() => decide(rejectCompanionRequest, r.request_id)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#E7E0D2] hover:border-[#EF4444] text-[#4B5563] hover:text-[#EF4444] text-xs font-medium transition disabled:opacity-50">
                    <X className="w-3.5 h-3.5" /> {t("community_page.reject")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {circleReqs.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-[0.16em] text-[#4B5563] mb-2">{t("community_page.circle_join_requests")}</div>
          <div className="flex flex-col gap-2">
            {circleReqs.map(({ circle, requests }) => requests.map((r) => (
              <div key={r.request_id} className="rounded-2xl bg-white border border-[#E7E0D2] p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`circle-request-${r.request_id}`}>
                <span className="text-sm text-[#1F2937]">{t("community_page.wants_to_join_pre", { name: r.user?.name || r.user?.email || t("community_page.someone") })} <strong>{circle.name}</strong></span>
                <div className="flex items-center gap-2">
                  <button disabled={busyId === r.request_id}
                    onClick={() => decide(acceptCircleJoinRequest, r.request_id, [circle.circle_id])}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white text-xs font-medium transition disabled:opacity-50">
                    <Check className="w-3.5 h-3.5" /> {t("community_page.accept")}
                  </button>
                  <button disabled={busyId === r.request_id}
                    onClick={() => decide(rejectCircleJoinRequest, r.request_id, [circle.circle_id])}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#E7E0D2] hover:border-[#EF4444] text-[#4B5563] hover:text-[#EF4444] text-xs font-medium transition disabled:opacity-50">
                    <X className="w-3.5 h-3.5" /> {t("community_page.reject")}
                  </button>
                </div>
              </div>
            )))}
          </div>
        </div>
      )}

      {invites.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-[0.16em] text-[#4B5563] mb-2">{t("community_page.circle_invites")}</div>
          <div className="flex flex-col gap-2">
            {invites.map((inv) => (
              <div key={inv.invite_id} className="rounded-2xl bg-white border border-[#E7E0D2] p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`circle-invite-${inv.invite_id}`}>
                <span className="text-sm text-[#1F2937]">{t("community_page.invited_to_join_pre")} <strong>{inv.circle?.name || t("community_page.a_circle")}</strong></span>
                <div className="flex items-center gap-2">
                  <button disabled={busyId === inv.invite_id}
                    onClick={() => decide(acceptCircleInvite, inv.invite_id)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white text-xs font-medium transition disabled:opacity-50">
                    <Check className="w-3.5 h-3.5" /> {t("community_page.accept")}
                  </button>
                  <button disabled={busyId === inv.invite_id}
                    onClick={() => decide(rejectCircleInvite, inv.invite_id)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#E7E0D2] hover:border-[#EF4444] text-[#4B5563] hover:text-[#EF4444] text-xs font-medium transition disabled:opacity-50">
                    <X className="w-3.5 h-3.5" /> {t("community_page.reject")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {orgReqs.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-[0.16em] text-[#4B5563] mb-2">{t("community_page.organization_join_requests")}</div>
          <div className="flex flex-col gap-2">
            {orgReqs.map(({ org, requests }) => requests.map((r) => (
              <div key={r.request_id} className="rounded-2xl bg-white border border-[#E7E0D2] p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`org-request-${r.request_id}`}>
                <span className="text-sm text-[#1F2937]">{t("community_page.wants_to_join_org", { name: r.from_user?.name || r.from_user?.email || t("community_page.someone"), org: org.name, role: r.requested_role })}</span>
                <div className="flex items-center gap-2">
                  <button disabled={busyId === r.request_id}
                    onClick={() => decide(acceptOrgJoinRequest, r.request_id, [org.org_id])}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#064E3B] hover:bg-[#047857] text-white text-xs font-medium transition disabled:opacity-50">
                    <Check className="w-3.5 h-3.5" /> {t("community_page.accept")}
                  </button>
                  <button disabled={busyId === r.request_id}
                    onClick={() => decide(rejectOrgJoinRequest, r.request_id, [org.org_id])}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#E7E0D2] hover:border-[#EF4444] text-[#4B5563] hover:text-[#EF4444] text-xs font-medium transition disabled:opacity-50">
                    <X className="w-3.5 h-3.5" /> {t("community_page.reject")}
                  </button>
                </div>
              </div>
            )))}
          </div>
        </div>
      )}
    </div>
  );
}

function FeedbackPanel() {
  const { t } = useI18n();
  const [items, setItems] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => listCommunityFeedback().then((r) => setItems(r.items || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!rating) return;
    setBusy(true);
    try {
      await submitCommunityFeedback({ rating, comment: comment.trim() || undefined });
      toast.success(t("community_page.toast_feedback_submitted"));
      setRating(0); setComment("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("community_page.toast_feedback_failed"));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="community-feedback-panel">
      <div className="rounded-2xl bg-white border border-[#E7E0D2] p-6">
        <div className="text-sm font-semibold text-[#1F2937]">{t("community_page.share_your_experience")}</div>
        <div className="mt-3 flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" data-testid={`community-feedback-star-${n}`} onClick={() => setRating(n)}
              className={`w-9 h-9 rounded-lg grid place-items-center transition ${n <= rating ? "bg-[#D97706] text-white" : "bg-white border border-[#E7E0D2] text-[#9CA3AF] hover:border-[#D97706] hover:text-[#D97706]"}`}>
              <Star className="w-4 h-4" fill={n <= rating ? "currentColor" : "none"} />
            </button>
          ))}
        </div>
        <textarea data-testid="community-feedback-comment" value={comment} onChange={(e) => setComment(e.target.value)}
          rows={3} maxLength={1000} placeholder={t("module_feedback.suggestions_placeholder")}
          className="mt-3 w-full px-3 py-2 rounded-lg border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm resize-none" />
        <button data-testid="community-feedback-submit" onClick={submit} disabled={busy || !rating}
          className="mt-3 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#064E3B] hover:bg-[#047857] disabled:opacity-40 text-white text-sm font-medium transition">
          <MessageSquare className="w-4 h-4" /> {t("community_page.submit_feedback")}
        </button>
      </div>

      <div className="mt-6 flex flex-col gap-3">
        {items === null ? (
          <div className="h-16 bg-[#E7E0D2] rounded-2xl animate-pulse" />
        ) : items.length === 0 ? (
          <div className="text-center text-sm text-[#4B5563] py-8">{t("community_page.no_feedback_yet")}</div>
        ) : (
          items.map((f) => (
            <div key={f.feedback_id} className="rounded-2xl bg-white border border-[#E7E0D2] p-4" data-testid={`community-feedback-${f.feedback_id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} className={`w-3.5 h-3.5 ${n <= f.rating ? "text-[#D97706] fill-[#D97706]" : "text-[#E7E0D2]"}`} />
                  ))}
                </div>
                {f.status !== "approved" && (
                  <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FFF7ED] text-[#D97706] border border-[#FED7AA]">
                    <Clock className="w-3 h-3" /> {t("community_page.pending_approval")}
                  </span>
                )}
              </div>
              {f.comment && <p className="mt-2 text-sm text-[#1F2937] italic">"{f.comment}"</p>}
              <p className="mt-1 text-xs text-[#4B5563]">{f.user_name || t("community_page.anonymous")}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
